// 261005 E1-P1 — chặng ② Khung xương: dựng · duyệt / bỏ duyệt / sửa tự động · 4 thống kê · vi phạm · bản chuyển thể
// · phải giữ khi viết · bảng nhân vật / bối cảnh / cao trào, chia sub-tab (không vào URL).
// Tách từ `AdaptationTab.tsx` :411-633 + `AdaptationSource.tsx` :283-297. Giữ nguyên lời gọi API và mã lỗi.
import { friendlyApiError, friendlyReasonText } from '../jobErrorText';
import React, { useEffect, useState } from 'react';
import { Alert, Card, Col, Collapse, Empty, Input, InputNumber, Modal, Row, Select, Space, Statistic, Table, Tabs, Tag, Tooltip, Typography, message } from 'antd';
import Button from '../WbButton';
import { adaptationApi } from '../../../../services/api/adaptation';
import type { OutlinePatchOp, OutlineViolation } from '../../../../types/adaptation';
import {
  beatsByEpisode, buildMustKeepView, canApprove, dedupeViolations, describePatchResult,
  describeRemainingViolations, gateLabel, groupViolations, splitIssuesByKind, summarizeOutline, withoutEpisodeClaimCodes,
} from '../../adaptationUtils';
import EntityTable, { EntityFocus } from '../EntityTable';
import StageFrame from '../StageFrame';
import OtherIssuesBlock, { OtherIssueRow } from '../OtherIssuesBlock';
import AcceptWarningModal, { AcceptTarget } from '../AcceptWarningModal';
import { acceptedLine, acceptedRowsOf, beatMaterialBadge, beatPlantCell, canOfferAccept, describeKeptApproval } from '../skeletonAcceptView';
import { useCtaHolds, useStageIntent, useWorkbenchCtx } from '../WorkbenchContext';
import { skeletonCardRed } from '../stageCtaView';
import { scrollWhenReady } from '../useScrollWhenReady';
import { decidePatchFlow } from '../skeletonPatchView';
import { skeletonAssistAction } from '../assistant/skeletonBlockView';
import OptionsButton from '../assistant/OptionsButton';
import { optionsDisabledReason, optionsRequest, warningSubject } from '../assistant/optionsAction';
import { skeletonChipsOf } from '../assistant/assistantView';
import { adaptationAssistantApi } from '../../../../services/api/adaptationAssistant';
import type { SkeletonIssueChip } from '../../../../types/adaptationAssistant';
import { askConfirm, handleAppendError } from '../appendRunActions';
import {
  continueDisabledReason, episodeCountBounds, episodeCountOver, REVIEW_CORE_PROMPT, stopOneAlertText,
} from '../appendRunView';
import { isNewStoryRun, nsStopOneControls, nsStopOneView } from '../newStoryRunView';
import { skeletonNeedsYouView, type SkeletonNeedsYouItem } from '../skeletonGateView';
import { dangerCopy, DangerAction, plainJobMessage, DangerCopyView, DangerCtx, SkeletonSubTab, violationTarget, WorkbenchTarget } from '../workbenchView';
// 261015 P4c-1 — tab "Tổng quan truyện" + chốt tổng quan (CAS) + tạo/gộp tổng quan.
import OverviewTab from '../overview/OverviewTab';
import { useOverviewJob } from '../useOverviewJob';
import { actionConfirm, approveBlock, approveView, hasManualClaims } from '../overview/overviewView';
import { errorShouldReload, overviewErrorView, type ErrorView } from '../overview/overviewErrors';
import type { OverviewMode } from '../../../../services/api/adaptation';
import ConsequenceModal from '../ConsequenceModal';
import type { ConsequenceView } from '../consequenceView';
import { unapproveCopy } from '../consequenceCopy';
import { budgetRiskView } from '../appendUnusedView';
import { Strip } from '../WbSlot';

const { Text, Paragraph } = Typography;

/** Hộp hỏi lại tĩnh (chữ từ `dangerCopy`). */
const confirmDanger = (action: DangerAction, ctx: DangerCtx, run: () => void | Promise<void>) => {
  const c = dangerCopy(action, ctx);
  Modal.confirm({ title: c.title, content: c.content, okText: c.okText, cancelText: 'Thôi', okButtonProps: { danger: c.danger }, onOk: () => run() });
};

/** Như `confirmDanger` nhưng nhận sẵn chữ (E1-P3a: `structureConfirm` của bảng Nhân vật/Bối cảnh). */
const confirmCopy = (c: DangerCopyView, run: () => void | Promise<void>) => {
  Modal.confirm({ title: c.title, content: c.content, okText: c.okText, cancelText: 'Thôi', okButtonProps: { danger: c.danger }, onOk: () => run() });
};

const SkeletonStage: React.FC = () => {
  const stageCta = useCtaHolds().holds('stage');   // 3h3: đỏ chỉ khi không có dải giữ việc
  const { storyId, workbench, data, refreshAll, busy: jobBusy, stageState, requestAssistant } = useWorkbenchCtx();
  const [epInput, setEpInput] = useState<number | null>(null);
  const [working, setWorking] = useState(false);
  const [genre, setGenre] = useState('');
  const [genreOpen, setGenreOpen] = useState(false);
  const [unapproveOpen, setUnapproveOpen] = useState(false);   // 3h4c: «hộp hậu quả» của Bỏ duyệt
  // 261015 P4c-1: tab mặc định = "Tổng quan truyện" khi truyện đã có / bắt buộc có tổng quan; truyện cũ giữ tab cũ (không đổi thói quen, không tự tốn AI).
  const [subTabRaw, setSubTab] = useState<SkeletonSubTab | null>(null);
  const [pendingRef, setPendingRef] = useState<{ ref: string; n: number } | null>(null);
  const [jumpEp, setJumpEp] = useState<number | null>(null);
  const { goTo } = useWorkbenchCtx();
  const [focus, setFocus] = useState<EntityFocus | null>(null);
  // 261015 P2b-1: hộp "Chấp nhận cảnh báo, có lý do" (một hàng cảnh báo đang được mở).
  const [accepting, setAccepting] = useState<AcceptTarget | null>(null);
  // E1-P3a — `focusEntities`: đổi sub-tab, đưa bộ lọc / hàng đích xuống bảng. Cuộn do `useStageIntent` (anchor chỉ tồn tại khi pane hiện).
  useStageIntent('skeleton', (t: WorkbenchTarget) => {
    if (t.intent === 'openOverview') { setSubTab('tong-quan-truyen'); return; }   // 261015 P4c-1: nextAction OVERVIEW_MISSING / OVERVIEW_NEEDS_FIX
    if (t.intent !== 'focusEntities') return;
    setSubTab(t.entityKind === 'location' ? 'boi-canh' : 'nhan-vat');
    setFocus({ id: t.entityId, filter: t.entityFilter, n: Date.now() });
  });

  const record = data.record;
  const outline = record?.outline ?? null;
  // F2: chip vi phạm do BE tính (GET scope ② — 0 AI) để nút ở mỗi vi phạm biết có "Nhờ AI sửa" (khớp chip) hay chỉ "Hỏi trợ lý". Lỗi/chưa tải ⇒ null ⇒ "Hỏi trợ lý".
  const [assistChips, setAssistChips] = useState<SkeletonIssueChip[] | null>(null);
  const violationsSig = JSON.stringify((record?.violations ?? []).map((v) => [v.gate, v.message]));
  useEffect(() => {
    if (!outline) { setAssistChips(null); return undefined; }
    let alive = true;
    adaptationAssistantApi.scope({ storyId, stageKey: 'skeleton', scopeKey: '' })
      .then((view) => { if (alive) setAssistChips(skeletonChipsOf(view)); })
      .catch(() => { if (alive) setAssistChips(null); });
    return () => { alive = false; };
  }, [storyId, !!outline, violationsSig]); // eslint-disable-line react-hooks/exhaustive-deps
  const locked = stageState('skeleton') === 'locked';
  const busy = working || jobBusy || locked;
  const approveV = approveView(record);
  const budgetV = budgetRiskView(workbench?.budgetRisk);   // 261102 P2: dải ngân sách review ở điểm dừng 1 (chỉ báo, không số tiền)
  const subTab: SkeletonSubTab = subTabRaw ?? (approveV.usesOverview ? 'tong-quan-truyen' : 'tong-quan');
  const overviewJob = useOverviewJob(storyId, workbench?.jobs.running, () => { refreshAll(); });

  // "Xem" vi phạm: đổi sub-tab TRƯỚC, cuộn + tô SAU khi tab đã render (không setTimeout mù).
  useEffect(() => {
    if (!pendingRef || subTab === 'tong-quan') return undefined;
    return scrollWhenReady(`outline-ref-${pendingRef.ref}`, () => setPendingRef(null));
  }, [subTab, pendingRef]);
  useEffect(() => {
    if (jumpEp === null) return;
    goTo({ stage: 'split', episode: jumpEp, intent: 'focusEpisode' });
    setJumpEp(null);
  }, [jumpEp, goTo]);

  const jumpToViolation = (refs: string[]) => {
    if (!refs.length) return;
    const t = violationTarget(refs[0], outline);
    if (t.stage === 'split') { setJumpEp(t.episode ?? null); return; }
    setSubTab(t.subTab ?? 'tong-quan');
    if (t.subTab && t.subTab !== 'tong-quan') setPendingRef({ ref: refs[0], n: Date.now() });
  };

  /**
   * Mọi lần ghi đều qua đây (E3-P1a): xem thử (miễn phí, không ghi) → so mốc màn hình → hỏi nếu ảnh hưởng tập đã viết/chia tập → PATCH kèm `expectedModifiedAt`.
   * "Huỷ" ở hộp hỏi ⇒ 0 PATCH. 409 `OUTLINE_CHANGED` ⇒ cảnh báo + tải lại. Quyết định ở `decidePatchFlow` (thuần, có test).
   */
  const patch = async (ops: OutlinePatchOp[]) => {
    if (locked) return;
    setWorking(true);
    const reload = async (msg: string) => { message.warning(msg, 6); await refreshAll(); };
    try {
      const preview = await adaptationApi.patchPreview(storyId, ops);
      const decision = decidePatchFlow(preview, record?.modifiedAt, outline?.params?.episodesStale === true);
      if (decision.kind === 'stale-view') { await reload('Khung xương vừa đổi ở nơi khác — đã tải lại, xem lại rồi thử lại'); return; }
      if (decision.kind === 'nothing-to-apply') { message.warning(describePatchResult(preview.applied, preview.rejected), 6); return; }
      if (decision.kind === 'confirm') {
        const ok = await new Promise<boolean>((resolve) => {
          const c = decision.copy;
          Modal.confirm({
            title: c.title, okText: c.okText, cancelText: c.cancelText,
            content: <div>{c.lines.map((l, i) => <p key={i} style={{ margin: '0 0 6px' }}>{l}</p>)}</div>,
            onOk: () => resolve(true), onCancel: () => resolve(false),
          });
        });
        if (!ok) return;
      }
      const statusBefore = record?.status;
      const r = await adaptationApi.patch(storyId, ops, preview.baseModifiedAt);
      const rec = await refreshAll();
      const grouped = groupViolations(splitIssuesByKind(withoutEpisodeClaimCodes(rec?.violations)).defects); // 261015 P1: đếm lỗi thật, không đếm thông báo/điểm
      const kept = describeKeptApproval(statusBefore, r);   // 261015 P2b-1 (C5): nói thật sửa này giữ "đã duyệt" hay kéo về nháp
      const text = `${describePatchResult(r.applied, r.rejected)} — ${describeRemainingViolations(grouped.errors.length, grouped.warnings.length)}${kept ? `. ${kept}` : ''}`;
      if (r.rejected.length) message.warning(text, 6);
      else message.success(text);
    } catch (e: any) {
      if (e?.response?.status === 409 && e?.response?.data?.code === 'OUTLINE_CHANGED') { await reload('Khung xương vừa thay đổi ở nơi khác — đã tải lại, thử lại'); return; }
      message.error(friendlyApiError(e, 'Không sửa được'));
    } finally { setWorking(false); }
  };

  /** Báo lỗi API của tổng quan / chốt: MỘT câu tiếng Việt + MỘT lối ra (hộp có nút làm ngay); lỗi do bản đang xem đã cũ ⇒ tự tải lại. */
  const reportOverviewError = async (e: unknown, fallback: string): Promise<ErrorView> => {
    const v = overviewErrorView(e, fallback);
    const extra = v.extra.map((x, i) => <p key={i} style={{ margin: '0 0 4px' }}>• {x}</p>);
    // Lỗi lint (400 + danh sách vi phạm) hiện ở tab "Cấu trúc chuyển thể" (đặc biệt truyện cũ: tab tổng quan chỉ là trạng thái trống); các lỗi OVERVIEW_* ở tab tổng quan.
    const goOverview = () => setSubTab(v.code === 'LINT' ? 'tong-quan' : 'tong-quan-truyen');
    if (v.exit === 'reload') message.warning(v.text, 7);
    else if (v.exit === 'none' || v.exit === 'wait') message.error(v.text, 8);
    else {
      Modal.confirm({
        title: 'Chưa thực hiện được', content: <div><p>{v.text}</p>{extra}</div>, okText: v.exitLabel, cancelText: 'Đóng',
        onOk: () => {
          goOverview();
          if (v.exit === 'regenerate') return runOverviewAction('full');
          if (v.exit === 'merge') return runOverviewAction('merge');
          return undefined;   // fix / askAssistant: chuyển sang tab tổng quan, các dải cảnh báo ở đầu tab chỉ đúng chỗ cần sửa
        },
      });
    }
    if (errorShouldReload(v)) await refreshAll();
    return v;
  };

  /** Hộp hỏi chi phí (+ cảnh báo ② đang duyệt / ý thêm tay) rồi gọi API tạo / tạo lại / đọc lại / gộp lại. Mốc `modifiedAt` = bản người đang xem (lệch ⇒ 409). */
  const runOverviewAction = async (mode: OverviewMode): Promise<void> => {
    if (locked || !record?.modifiedAt) return;
    const c = actionConfirm(mode, { status: record.status, hasOverview: !!outline?.overview, estimate: record.overviewState?.estimate, hasManualClaims: hasManualClaims(outline?.overview) });
    const ok = await askConfirm({ title: c.title, okText: c.okText, danger: c.danger, content: <div>{c.lines.map((l, i) => <p key={i} style={{ margin: '0 0 6px' }}>{l}</p>)}</div> });
    if (!ok) return;
    try { await overviewJob.start(mode, record.modifiedAt); await refreshAll(); } catch (e: any) { await reportOverviewError(e, 'Không chạy được'); }
  };

  /** Lưu form sửa tổng quan: MỘT PATCH nhiều lệnh, CAS bằng `modifiedAt` bản đang xem. Trả true khi có ít nhất một lệnh được áp (đóng hộp). */
  const patchOverview = async (ops: OutlinePatchOp[]): Promise<boolean> => {
    if (locked || !record?.modifiedAt) return false;
    setWorking(true);
    try {
      const r = await adaptationApi.patch(storyId, ops, record.modifiedAt);
      await refreshAll();
      if (r.rejected.length) message.warning(describePatchResult(r.applied, r.rejected), 9);
      else message.success('Đã lưu — khung xương về bản nháp, cần chốt tổng quan lại', 5);
      return r.applied.length > 0;
    } catch (e: any) {
      await reportOverviewError(e, 'Không lưu được');
      return false;
    } finally { setWorking(false); }
  };

  const doApprove = async () => {
    if (locked) return;
    if (!record?.modifiedAt) { message.warning('Chưa có mốc của bản đang xem — đã tải lại, thử lại', 5); await refreshAll(); return; }
    setWorking(true);
    try {
      await adaptationApi.approve(storyId, record.modifiedAt);   // 261015 P4c-1: CAS bắt buộc (thiếu ⇒ 400)
      message.success(approveV.doneText);
      await refreshAll();
    } catch (e: any) {
      await reportOverviewError(e, 'Không duyệt được');
    } finally { setWorking(false); }
  };

  /** E4-P4b STOP 1: "Duyệt & chạy tiếp" (BE tự duyệt rồi chạy bồi tên → chia tập). 261015 P4c-1 (Q1): gửi mốc bản đang xem — BE chốt NGUYÊN TỬ đúng bản đó. */
  const doContinue = async () => {
    if (locked) return;
    if (!record?.modifiedAt) { message.warning('Chưa có mốc của bản đang xem — đã tải lại, thử lại', 5); await refreshAll(); return; }
    setWorking(true);
    try {
      await adaptationApi.appendRunContinue(storyId, record.modifiedAt);
      message.success(isNewStoryRun(workbench?.appendRun) ? 'Đã duyệt — máy chạy tiếp chặng 2 ở nền (bản địa hoá, chia tập, viết)' : approveV.usesOverview ? 'Đã chốt tổng quan — đang đổi tên và chia tập mới ở nền' : 'Đã duyệt khung xương — đang đổi tên và chia tập mới ở nền');
      await refreshAll();
    } catch (e: any) {
      const code = e?.response?.data?.code;
      if (typeof code === 'string' && (code.startsWith('OVERVIEW_') || code === 'OUTLINE_CHANGED' || code === 'EXPECTED_MODIFIED_AT_REQUIRED' || code === 'INVALID_EXPECTED_MODIFIED_AT')) await reportOverviewError(e, 'Không chạy tiếp được');
      else await handleAppendError(e, { goTo, refreshAll });
    } finally { setWorking(false); }
  };

  /**
   * Bỏ duyệt. `expectedModifiedAt` = mốc BE trả cùng «hộp hậu quả» (`base.outlineModifiedAt`: đúng bản người đã xem hệ quả); không có ⇒ mốc bản đang tải.
   * Lỗi được NÉM RA cho hộp: 409 `OUTLINE_CHANGED` ⇒ hộp tải lại hệ quả; lỗi khác ⇒ `reportOverviewError` như cũ.
   */
  const doUnapprove = async (view: ConsequenceView | null) => {
    const base = view?.base.outlineModifiedAt;
    const expected = typeof base === 'string' && base ? base : record?.modifiedAt;
    if (!expected) { message.warning('Chưa có mốc của bản đang xem — đã tải lại, thử lại', 5); await refreshAll(); return; }
    setWorking(true);
    try { await adaptationApi.unapprove(storyId, expected); await refreshAll(); }
    finally { setWorking(false); }
  };

  /**
   * 261015: "Kiểm lại & sửa" là vòng sửa DANH SÁCH TẬP — chưa chia tập (hoặc tập lỗi thời) thì không có gì để sửa. Prod Reborn 2: bấm khi 0 tập
   * ⇒ lưu lỗi ORPHAN_ENTITY/EPISODE_COUNT giả ⇒ nút Duyệt khoá, mà Chia tập lại đòi đã duyệt. Cùng điều kiện với nút `repair_episodes` của Trợ lý ②.
   */
  const repairBlock = !(outline?.episodes?.length)
    ? 'Chưa chia tập — duyệt khung xương rồi chia tập ở bước Chia tập; nút này chỉ sửa danh sách tập đã có'
    : outline?.params?.episodesStale === true ? 'Danh sách tập đang lỗi thời — chia lại tập ở bước Chia tập trước' : undefined;

  const doRepair = async () => {
    if (locked || repairBlock) return;
    setWorking(true);
    try {
      const r = await adaptationApi.repair(storyId);
      message.info(plainJobMessage(r.message) || 'Đang kiểm lại ở nền — tải lại sau ít phút');
      await refreshAll();
    } catch (e: any) {
      message.error(friendlyApiError(e, 'Không chạy được'));
    } finally { setWorking(false); }
  };

  const doBuild = async () => {
    if (locked) return;
    setWorking(true);
    try {
      const r = await adaptationApi.buildSkeleton(storyId, { genre: genre.trim() || 'chưa rõ' });
      message.success(plainJobMessage(r?.message) || 'Đã bắt đầu — chạy ở nền', 5);
      setGenreOpen(false);
      await refreshAll();
    } catch (e: any) {
      message.error(friendlyApiError(e, 'Không chạy được'), 8);
    } finally { setWorking(false); }
  };

  const build = () => {
    // Dựng lại khi đã có khung xương = hộp hỏi lại MỚI (code cũ không hỏi).
    if (outline) confirmDanger('rebuildSkeleton', {}, doBuild);
    else doBuild();
  };

  const header = (
    <StageFrame
      stage="skeleton"
      redo={outline ? { label: 'Dựng lại khung xương', run: () => setGenreOpen(true), disabled: busy } : undefined}
      gate={undefined}
    >
      {!outline ? (
        <>
          <div id="section-genre">
            <Space wrap>
              <Input
                placeholder="Thể loại (vd: ngôn tình học đường)" value={genre} style={{ width: 280 }} disabled={busy}
                onChange={(e) => setGenre(e.target.value)}
              />
              <Button type="primary" cta={stageCta} disabled={busy} onClick={build}>Dựng khung xương</Button>
            </Space>
          </div>
          <Empty description="Chưa có khung xương — quét truyện xong thì bấm Dựng khung xương" />
        </>
      ) : (
        renderBody()
      )}
      <AcceptWarningModal
        target={accepting} busy={working} nameOf={(id) => id} onCancel={() => setAccepting(null)}
        onSubmit={async (ops) => { setAccepting(null); await patch(ops); }}
      />
      <ConsequenceModal
        open={unapproveOpen} onClose={() => setUnapproveOpen(false)} danger
        title={unapproveCopy(approveV.usesOverview).title} lead={unapproveCopy(approveV.usesOverview).lead} okText={unapproveCopy(approveV.usesOverview).okText}
        load={() => adaptationApi.unapproveImpact(storyId)}
        fallback={<p>{unapproveCopy(approveV.usesOverview).fallback}</p>}
        errorFallback="Không bỏ duyệt được"
        onConfirm={doUnapprove}
        onError={(e) => { void reportOverviewError(e, 'Không bỏ duyệt được'); }}
      />
      <Modal
        open={genreOpen} title="Dựng khung xương" okText="Dựng" cancelText="Thôi" onCancel={() => setGenreOpen(false)}
        onOk={build} confirmLoading={working} okButtonProps={{ disabled: busy }}
      >
        <Input
          placeholder="Thể loại (vd: ngôn tình học đường)" value={genre} onChange={(e) => setGenre(e.target.value)}
        />
      </Modal>
    </StageFrame>
  );

  function renderBody() {
    if (!outline || !record) return null;
    const violations: OutlineViolation[] = dedupeViolations(withoutEpisodeClaimCodes(record.violations ?? []));   // 261015 P3a: mã ý của tập là của ④
    const summary = summarizeOutline(outline);
    // 261015 P1: dải chỉ hiện LỖI THẬT; thông báo/điểm/… ở khối gập. Khoá Duyệt (`canApprove`) vẫn dựa TOÀN BỘ ERROR — không đổi.
    const { defects, others } = splitIssuesByKind(violations);
    const otherRows: OtherIssueRow[] = (['score', 'notice', 'unchecked', 'stale'] as const).flatMap((kind) => others[kind].map((v) => ({ gate: v.gate, message: v.message, kind })));
    // 261015 P2b-1: nhóm "Đã chấp nhận" — mã · ref · lý do · người · lúc + nút "Bỏ chấp nhận" (BE tách khỏi dải lỗi lúc đọc; không chip).
    const nameOfBeat = (id: string): string => id;
    for (const row of acceptedRowsOf(others.accepted)) {
      otherRows.push({
        gate: row.gate, kind: 'accepted', plain: true, message: acceptedLine(row, nameOfBeat),
        action: <Button size="small" type="link" disabled={busy} onClick={() => patch([row.revokeOp])}>Bỏ chấp nhận</Button>,
      });
    }
    const grouped = groupViolations(defects);
    const approval = canApprove(outline, violations);
    const approved = record.status === 'APPROVED';
    const mustKeep = buildMustKeepView(outline);
    const cells = beatsByEpisode(outline.beats, outline.params.episodes);
    const appendRun = workbench?.appendRun ?? null;
    const stop1 = appendRun?.status === 'WAIT_SKELETON' ? appendRun : null;
    const stopCtl = nsStopOneControls(stop1);   // 261023 E3-P3c2b (H13): chuỗi truyện mới ở STOP 1 chỉ có MỘT nút chính, ở dải — thẻ này ẩn nút «chạy tiếp», thẻ Tổng quan ẩn nút «Chốt tổng quan»
    const stop1New = stop1 ? nsStopOneView(stop1, outline.params.episodes) : null;   // 261023 E3-P3c1: chuỗi truyện mới ⇒ chữ riêng
    const bounds = stop1 ? episodeCountBounds({ episodesLen: outline.episodes?.length ?? 0, lockedThrough: stop1.lockedThrough, stale: outline.params?.episodesStale === true, chapterUnitMax: workbench?.chapterUnitMax }) : null;
    const epShown = epInput ?? outline.params.episodes;
    const epOver = bounds ? episodeCountOver(epShown, bounds.max) : false;   // 3g Q5: vượt số chương ⇒ ô đỏ + khoá «Sửa số tập» (không tự kẹp giá trị)
    // 261015 P4c-1: khoá nút Duyệt/Chốt = lỗi khung xương TRƯỚC, rồi lý do tổng quan (thiếu quyển → nguồn lệch → khung xương đổi → tham chiếu hỏng → ý chưa xác minh).
    const blockReason = approveBlock(approval, approveV);
    const continueBlock = continueDisabledReason({ canApprove: !blockReason, reason: blockReason ?? undefined });
    // R4/F1: thẻ duyệt (ngoài các tab) thắng; nút «Tạo/Chốt tổng quan» ở tab Tổng quan nhường khi thẻ đang đỏ — đúng một nút đỏ mỗi bước.
    const cardRed = skeletonCardRed({
      approved, stop1: !!stop1, cardContinueShow: stopCtl.cardContinue.show, cardContinuePrimary: stopCtl.cardContinue.primary,
      cardApproveEnabled: !busy && !blockReason, cardContinueEnabled: !busy && !continueBlock,   // R5: thẻ bị khoá (vd thiếu tổng quan) thì nhường nút ở tab Tổng quan
    });
    const confirmUnapprove = () => { if (!locked) setUnapproveOpen(true); };   // 3h4c: hộp hậu quả (BE) thay hộp hỏi tĩnh; preview lỗi ⇒ hộp cũ

    const overview = (
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Row gutter={12}>
          <Col span={6}><Card size="small">
            <Statistic
              title="Nhân vật" value={summary.charactersTotal}
              styles={summary.noLead ? { content: { color: 'var(--wb-bad)' } } : undefined}
            />
            <Text type="secondary" style={{ fontSize: 13 }}>
              Chính {summary.leads} · Phụ {summary.support} · Chức năng {summary.functional}
            </Text>
          </Card></Col>
          <Col span={6}><Card size="small">
            <Statistic title="Bối cảnh chính" value={summary.primaryLocations} />
            <Text type="secondary" style={{ fontSize: 13 }}>Bối cảnh phụ: {summary.otherLocations}</Text>
          </Card></Col>
          <Col span={6}><Card size="small">
            <Statistic title="Cao trào" value={summary.beats} />
            <Text type="secondary" style={{ fontSize: 13 }}>Trong đó {summary.majorBeats} cao trào lớn</Text>
          </Card></Col>
          <Col span={6}><Card size="small">
            <Statistic title="Số tập" value={summary.episodesPlanned} />
            <Text type="secondary" style={{ fontSize: 13 }}>Đã viết {summary.episodesWritten} tập</Text>
          </Card></Col>
        </Row>

        {defects.length > 0 && (
          <Card size="small" title={`Còn ${grouped.errors.length} lỗi · ${grouped.warnings.length} cảnh báo`}>
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {defects.map((v, i) => (
                <Alert
                  key={`${v.gate}-${i}`} type={v.severity === 'ERROR' ? 'error' : 'warning'}
                  message={gateLabel(v.gate)} description={<Text style={{ fontSize: 13 }}>{friendlyReasonText(v.message)}</Text>}
                  action={(
                    <Space size={4} wrap>
                      {v.refs.length > 0 && <Button size="small" type="link" onClick={() => jumpToViolation(v.refs)}>Xem</Button>}
                      {/* 261015 P2b-1: chấp nhận có lý do — chỉ 3 mã WARN (ERROR không bao giờ); BE lưu người/lúc/dấu nội dung. */}
                      {canOfferAccept(v) && <Button size="small" type="link" disabled={busy} onClick={() => setAccepting({ gate: v.gate, refs: v.refs, message: v.message })}>Chấp nhận…</Button>}
                      {/* E3-P1b: khung Trợ lý tra id chip của BE (gate + message); không khớp ⇒ điền câu hỏi thay vì tick. */}
                      <Button size="small" type="link" onClick={() => requestAssistant(skeletonAssistAction(v, assistChips).intent)}>{skeletonAssistAction(v, assistChips).label}</Button>
                      {/* E3-P4b2: 1 cú bấm — khung tick chip + tự gửi "3 phương án" (chip không khớp ⇒ gửi bằng chữ). */}
                      <OptionsButton
                        disabledReason={optionsDisabledReason({ stageLocked: locked })}
                        onClick={() => requestAssistant(optionsRequest(skeletonAssistAction(v, assistChips).intent, warningSubject('khung xương', v)))}
                      />
                    </Space>
                  )}
                />
              ))}
            </Space>
          </Card>
        )}
        <OtherIssuesBlock rows={otherRows} />

        <Card size="small" title="Bản chuyển thể">
          <Paragraph><Text strong>Cốt lõi: </Text>{outline.adaptation?.core}</Paragraph>
          <Collapse
            size="small"
            items={[
              { key: 'keep', label: `Giữ lại (${outline.adaptation?.keep?.length ?? 0})`,
                children: <ul>{(outline.adaptation?.keep ?? []).map((x, i) => <li key={i}><Text strong>{x.what}</Text> — {x.why}</li>)}</ul> },
              { key: 'cut', label: `Đã cắt (${outline.adaptation?.cut?.length ?? 0})`,
                children: <ul>{(outline.adaptation?.cut ?? []).map((x, i) => <li key={i}><Text strong>{x.what}</Text> — {x.why}</li>)}</ul> },
              { key: 'merge', label: `Đã gộp (${outline.adaptation?.merge?.length ?? 0})`,
                children: <ul>{(outline.adaptation?.merge ?? []).map((x, i) => <li key={i}><Text strong>{x.what}</Text> — {x.why}</li>)}</ul> },
            ]}
          />
        </Card>
      </Space>
    );

    const mustKeepPane = mustKeep && (
      <Card size="small" title="Phải giữ khi viết (AI đề xuất — bạn duyệt)">
        <Paragraph type="secondary" style={{ fontSize: 13 }}>Bỏ một mục sẽ đưa khung xương về nháp (cần duyệt lại).</Paragraph>
        {mustKeep.empty ? <Text type="secondary">AI không đề xuất gì</Text> : (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {mustKeep.moments.length > 0 && (
              <div>
                <Text strong>Khoảnh khắc bắt buộc ({mustKeep.moments.length})</Text>
                {mustKeep.moments.map((m) => (
                  <div key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 6 }}>
                    <div style={{ flex: 1 }}>
                      <Tag>{m.kindLabel}</Tag><Text type="secondary">Ch. {m.chapter}</Text> · {m.what}
                      {m.characterNames.length > 0 && <Text type="secondary"> · {m.characterNames.join(', ')}</Text>}
                      {m.evidence
                        ? <div><Text type="secondary" italic style={{ fontSize: 13 }}>“{m.evidence}”{m.anchored ? ` · chương ${m.chapter} · đã xác minh` : ''}</Text></div>
                        : <div><Text type="secondary" style={{ fontSize: 13, opacity: 0.7 }}>chưa có trích nguồn xác minh</Text></div>}
                    </div>
                    <Button size="small" danger disabled={busy} onClick={() => confirmDanger('removeEntity', { name: 'khoảnh khắc này' }, () => patch([{ op: 'removeRequiredMoment', id: m.id }]))}>Bỏ</Button>
                  </div>
                ))}
              </div>
            )}
            {mustKeep.pairs.length > 0 && (
              <div>
                <Text strong>Cặp giữ riêng — không gộp ({mustKeep.pairs.length})</Text>
                {mustKeep.pairs.map((p) => (
                  <div key={`${p.a}|${p.b}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 6 }}>
                    <div style={{ flex: 1 }}>
                      <Text strong>{p.aName} — {p.bName}</Text> · {p.reason}
                      {p.evidence && <div><Text type="secondary" italic style={{ fontSize: 13 }}>“{p.evidence}”</Text></div>}
                    </div>
                    <Button size="small" danger disabled={busy} onClick={() => confirmDanger('removeEntity', { name: 'cặp này' }, () => patch([{ op: 'removeKeepSeparate', a: p.a, b: p.b }]))}>Bỏ</Button>
                  </div>
                ))}
              </div>
            )}
          </Space>
        )}
      </Card>
    );

    const items = [
      { key: 'tong-quan-truyen', label: 'Tổng quan truyện', children: (
        <OverviewTab
          storyId={storyId} record={record} workbench={workbench} job={overviewJob} busy={busy} approve={approveV} lint={approval}
          onApprove={doApprove} onUnapprove={confirmUnapprove} patchOverview={patchOverview} runAction={runOverviewAction}
          requestAssistant={requestAssistant} openStructure={() => setSubTab('tong-quan')} stop1={!!stop1} showApprove={stopCtl.overviewApprove.show} cardHoldsCta={cardRed}
        />
      ) },
      { key: 'tong-quan', label: 'Cấu trúc chuyển thể', children: overview },
      { key: 'nhan-vat', label: `Nhân vật (${outline.characters.length})`, destroyOnHidden: true, children: (
        <EntityTable kind="character" focus={focus} onFocusConsumed={() => setFocus(null)} onPatch={patch}
          working={working} setWorking={setWorking} confirmCopy={confirmCopy} />
      ) },
      { key: 'boi-canh', label: `Bối cảnh (${outline.locations.length})`, destroyOnHidden: true, children: (
        <EntityTable kind="location" focus={focus} onFocusConsumed={() => setFocus(null)} onPatch={patch}
          working={working} setWorking={setWorking} confirmCopy={confirmCopy} />
      ) },
      { key: 'cao-trao', label: 'Cao trào', children: (
        <>
          <Space wrap size={4} style={{ marginBottom: 12 }}>
            {cells.map((c) => (
              <Tag key={c.ep} color={c.hasMajor ? 'red' : c.beats.length ? 'orange' : 'default'} style={{ minWidth: 34, textAlign: 'center' }}>{c.ep}</Tag>
            ))}
          </Space>
          <Table
            rowKey="id" size="small" pagination={false} dataSource={outline.beats}
            onRow={(r: any) => ({ id: `outline-ref-${r.id}` })}
            columns={[
              { title: 'Loại', dataIndex: 'type', key: 'type', width: 150, render: (v: string, r: any) => {
                // 261015 P2b-3: cao trào do `addBeatFromMaterial` thêm từ vật liệu quét ⇒ huy hiệu (logic ở `beatMaterialBadge`, thuần, có jest).
                const mb = beatMaterialBadge(r);
                return mb ? <span title={mb.title}>{v}<Tag color="blue" style={{ marginLeft: 6 }}>{mb.text}</Tag></span> : v;
              } },
              { title: 'Tập', dataIndex: 'episode', key: 'episode', width: 110, render: (v: number, r: any) => (
                <InputNumber
                  size="small" min={1} max={outline.params.episodes} value={v} disabled={busy}
                  onChange={(ep) => { if (ep && ep !== v) patch([{ op: 'moveBeat', id: r.id, episode: ep as number }]); }}
                />
              ) },
              { title: 'Mức', dataIndex: 'weight', key: 'weight', width: 130, render: (v: string, r: any) => (
                <Select
                  size="small" value={v} style={{ width: 110 }} disabled={busy}
                  onChange={(w) => patch([{ op: 'setBeatWeight', id: r.id, weight: w as any }])}
                  options={[{ value: 'major', label: 'Lớn' }, { value: 'minor', label: 'Thường' }]}
                />
              ) },
              { title: 'Gieo', dataIndex: 'setup', key: 'setup', ellipsis: true },
              { title: 'Chi tiết gieo', dataIndex: 'plant', key: 'plant', ellipsis: true, render: (_v: string, r: any) => {
                // 261015 P2b-1: có neo ⇒ «trích ≤ 80 ký tự» · chương N; ADDED ⇒ huy hiệu "do chuyển thể thêm" (lý do ở tooltip). Logic ở `beatPlantCell` (thuần, có jest).
                const c = beatPlantCell(r);
                if (c.kind === 'none') return null;
                if (c.kind === 'missing') return <span style={{ opacity: 0.6 }}>chưa có</span>;
                return (
                  <span title={c.title}>
                    {c.text}
                    {c.badge && <Tag color="gold" style={{ marginLeft: 6 }}>{c.badge}</Tag>}
                    {c.quoteText && <div><Text type="secondary" italic style={{ fontSize: 13 }}>{c.quoteText} · {c.chapterText}</Text></div>}
                  </span>
                );
              } },
              { title: 'Trả', dataIndex: 'payoff', key: 'payoff', ellipsis: true },
            ] as any}
          />
        </>
      ) },
      ...(mustKeepPane ? [{ key: 'phai-giu', label: 'Phải giữ khi viết', children: mustKeepPane }] : []),
    ];

    return (
      <>
        {stop1 && bounds && (
          <Alert
            style={{ marginBottom: 12 }} type="info" showIcon message={stop1New ? stop1New.message : stopOneAlertText(stop1)}
            description={(
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <Text>{stop1New ? stop1New.episodesText : `Số tập: ${stop1.episodesBefore ?? '?'} → ${stop1.episodesAfter ?? outline.params.episodes} tập (AI đề xuất) — sửa ở ô Số tập trước khi duyệt.`}</Text>
                <Space wrap>
                  <InputNumber size="small" min={bounds.min} value={epShown} status={epOver ? 'error' : undefined} disabled={busy} onChange={(v) => setEpInput(typeof v === 'number' ? v : null)} aria-label="Số tập" />
                  <Button size="small" disabled={busy || epOver || epInput === null || epInput === outline.params.episodes}
                    onClick={async () => { if (epInput !== null) { await patch([{ op: 'setEpisodeCount', episodes: epInput }]); setEpInput(null); } }}>Sửa số tập</Button>
                  <Text type={epOver ? 'danger' : 'secondary'} style={{ fontSize: 13 }}>{bounds.help}</Text>
                </Space>
                {!stop1New && <Text type="secondary">Cốt lõi và các phần đã giữ / đã cắt vẫn dựa trên phần cũ — nhờ Trợ lý AI xem lại nếu cần.</Text>}
                {!stop1New && (
                  <div>
                    <Button size="small" onClick={() => requestAssistant({ stageKey: 'skeleton', issueIds: [], suggestedText: REVIEW_CORE_PROMPT })}>Nhờ AI xem lại cốt lõi</Button>
                  </div>
                )}
              </Space>
            )}
          />
        )}
        <div id="section-approve">
          <Alert
            type={approved ? 'success' : approval.can ? 'info' : 'warning'} showIcon
            message={approved ? (approveV.usesOverview ? 'Tổng quan ĐÃ CHỐT' : 'Khung xương ĐÃ DUYỆT') : (approveV.usesOverview ? 'Bản nháp — chưa chốt tổng quan' : 'Bản nháp — chưa duyệt')}
            description={(
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text>
                  {approved
                    ? 'Đã có thể viết phân tập. Sửa gieo có trích nguồn, sửa trích khoảnh khắc và chấp nhận cảnh báo giữ nguyên “đã duyệt”; mọi lần sửa khác bên dưới (kể cả sửa tổng quan) sẽ đưa bản này về lại trạng thái nháp.'
                    : blockReason
                      ? blockReason
                      : stopCtl.draftHint
                        ? stopCtl.draftHint.map((p, i) => (p.bold ? <b key={i}>{p.text}</b> : <React.Fragment key={i}>{p.text}</React.Fragment>))
                      : approveV.usesOverview
                        ? 'Không còn lỗi nào — đọc lại tổng quan rồi bấm “Chốt tổng quan” để mở đường chia tập. Chốt là xác nhận bạn hiểu nguyên tác (bản tổng quan này) VÀ phương án chuyển thể (khung xương này).'
                        : 'Không còn lỗi nào — xem lại rồi bấm duyệt để mở đường viết phân tập.'}
                </Text>
                {budgetV && <Strip tone="warn" role="alert"><b style={{ color: 'var(--wb-text)' }}>{budgetV.headline}</b><br />{budgetV.detail}</Strip>}
                <Space wrap>
                  {approved
                    ? <Button onClick={confirmUnapprove} disabled={busy}>{approveV.usesOverview ? approveV.unapproveLabel : 'Bỏ duyệt'}</Button>
                    : stop1
                      ? (stopCtl.cardContinue.show
                        ? (
                          <>
                          <Tooltip title={continueBlock ?? (approveV.usesOverview ? 'Chốt tổng quan rồi tự chạy: đổi tên nhân vật mới → chia tập mới' : 'Duyệt khung xương rồi tự chạy: đổi tên nhân vật mới → chia tập mới')}>
                            <span><Button type={stopCtl.cardContinue.primary ? 'primary' : 'default'} cta={stageCta} onClick={doContinue} disabled={busy || !!continueBlock}>{approveV.continueLabel}</Button></span>
                          </Tooltip>
                          {stopCtl.cardContinue.hint && <Text type="secondary">{stopCtl.cardContinue.hint}</Text>}
                          </>
                        )
                        : null)
                      : (
                        <Tooltip title={blockReason ?? undefined}>
                          <span><Button type="primary" cta={stageCta} onClick={doApprove} disabled={busy || !!blockReason}>{approveV.approveLabel}</Button></span>
                        </Tooltip>
                      )}
                  <Tooltip title={repairBlock}>
                    <span><Button onClick={() => confirmDanger('repair', {}, doRepair)} disabled={busy || !!repairBlock}>Kiểm lại &amp; sửa tự động</Button></span>
                  </Tooltip>
                  <Button onClick={() => { refreshAll(); }} disabled={working}>Tải lại</Button>
                </Space>
              </Space>
            )}
          />
        </div>
        {needsYouBlock()}
        <Tabs activeKey={subTab} onChange={(k) => setSubTab(k as SkeletonSubTab)} items={items} />
      </>
    );
  }

  /**
   * 3d2 (K13): kết quả cổng ② «máy chấm khung xương» (chuỗi truyện mới). Chỉ là gợi ý đọc — KHÔNG đụng `canApprove`/nút Duyệt.
   * FRESH ⇒ «Cần bạn xem» (nhãn chiều + vấn đề + trích dẫn gập + «Đi tới»); STALE / GATE_ERROR ⇒ một dòng xám, không có nút «Chấm lại» (ngoài phạm vi).
   */
  function needsYouBlock() {
    const v = skeletonNeedsYouView(workbench?.skeletonGate, { approved: workbench?.outlineFlags.status === 'APPROVED' });
    if (v.greyLine) return <Text type="secondary" style={{ display: 'block', margin: '8px 0', fontSize: 13 }}>{v.greyLine}</Text>;
    if (!v.items.length) return null;
    const goToUnit = (it: SkeletonNeedsYouItem) => {
      if (it.unit.kind === 'character') setSubTab('nhan-vat');
      else if (it.unit.kind === 'beat') setSubTab('cao-trao');
      else setSubTab('tong-quan');
      if (it.unit.id) setPendingRef({ ref: it.unit.id, n: Date.now() });
    };
    return (
      <Card size="small" title={`Cần bạn xem (${v.items.length})`} style={{ margin: '8px 0' }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {v.items.map((it) => (
            <div key={it.key}>
              <Space size={8} wrap>
                <Tag>{it.dimensionLabel}</Tag>
                <Text>{it.problem}</Text>
                {it.unit.kind !== 'unknown' && <Button size="small" type="link" onClick={() => goToUnit(it)}>Đi tới</Button>}
              </Space>
              {it.quotes.length > 0 && (
                <Collapse
                  size="small" ghost
                  items={[{ key: 'q', label: 'Trích dẫn nguyên tác', children: <ul>{it.quotes.map((q, i) => <li key={i}><Text type="secondary" style={{ fontSize: 13 }}>{q}</Text></li>)}</ul> }]}
                />
              )}
            </div>
          ))}
        </Space>
      </Card>
    );
  }

  return header;
};

export default SkeletonStage;
